import { Add, Article, Construction, Map, People } from "@mui/icons-material";
import {
  Box,
  Dropdown,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  TabList,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Outlet, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { ApiCompetition, ApiLeague, ApiLeagueSummary } from "griptonite-api-client";
import { orderBy } from "lodash";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import {
  useCompetitionQuery,
  useLeagueQuery,
  useVenueCompetitionsQuery,
  useVenueLeaguesQuery,
} from "@/hooks/competitions";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";

import formatPeriod from "../../utils/formatPeriod";
import {
  CompetitionCard,
  CompetitionStatusChip,
  getCompetitionStatusLabel,
  LeagueCard,
} from "../CompetitionCard";
import { PillsSelect } from "../forms/PillsSelect";
import { ButtonLink, TabLink } from "../Links";
import { useDisplayGenericModal } from "../modals/useModal";
import { useCompetitionContext } from "./CompetitionProvider";
import EventsListToggle, {
  EVENTS_LIST_ID,
  useCollapseEventsListOnSelect,
} from "./EventsListToggle";

const CompetitionOverview = ({
  competitionUuid,
  initialTab = "profile",
  leagueUuid,
  children,
}: {
  competitionUuid?: string;
  initialTab?: "profile" | "routes" | "competitors" | "tools";
  children?: ReactNode;
  leagueUuid?: string;
}) => {
  const currentVenueUuid = useCurrentVenueUuid();

  const { data: venue } = useCurrentVenueQuery();

  const { data: competition } = useCompetitionQuery(competitionUuid, currentVenueUuid);

  const { data: league } = useLeagueQuery(leagueUuid, currentVenueUuid);

  let activeTab = initialTab;

  // oxlint-disable-next-line prefer-const
  let { competitionSelectorOpen, setCompetitionSelectorOpen } = useCompetitionContext();

  useCollapseEventsListOnSelect(competitionUuid, () => setCompetitionSelectorOpen(false));

  const matchRoute = useMatchRoute();
  const isCreate = !!matchRoute({
    to: "/gym/$venueUuid/events/create",
    fuzzy: true,
  });
  if (
    matchRoute({
      to: "/gym/$venueUuid/events/$competitionUuid/routes",
      fuzzy: true,
    })
  ) {
    activeTab = "routes";
  } else if (
    matchRoute({
      to: "/gym/$venueUuid/events/$competitionUuid/competitors",
      fuzzy: true,
    })
  ) {
    activeTab = "competitors";
  } else if (
    matchRoute({
      to: "/gym/$venueUuid/events/$competitionUuid/tools",
      fuzzy: true,
    })
  ) {
    activeTab = "tools";
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        flexDirection: "column",
      }}
    >
      <Tabs value={activeTab}>
        <TabList
          sx={{
            flex: 1,
            ".MuiTab-root": {
              alignSelf: "flex-end",
            },
          }}
        >
          {isCreate || competition ? (
            <Box
              sx={{
                width: 350,
                backgroundColor: "neutral.100",
                marginX: 1,
                marginY: 0.5,
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "neutral.200",
                borderRadius: 8,
                paddingX: 1,
                cursor: "pointer",
                overflow: "hidden",
              }}
              onClick={() => setCompetitionSelectorOpen(!competitionSelectorOpen)}
            >
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="baseline">
                  <Typography
                    level="body-lg"
                    fontWeight="bold"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {competition?.name ??
                      (isCreate
                        ? league
                          ? "Creating New Round"
                          : "Creating New Competition"
                        : "Events")}
                  </Typography>
                  {competition && (
                    <CompetitionStatusChip status={competition.status} variant="soft" />
                  )}
                </Stack>
                <Stack direction="row">
                  {(competition?.league || league) && (
                    <Typography
                      level="body-md"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {(competition?.league || league)!.name}
                    </Typography>
                  )}
                  <Typography level="body-sm" color="neutral">
                    {competition ? formatPeriod(competition, venue?.timezone || "UTC") : "Upcoming"}
                  </Typography>
                </Stack>
              </Stack>
              <EventsListToggle
                isOpen={competitionSelectorOpen}
                onToggle={() => setCompetitionSelectorOpen(!competitionSelectorOpen)}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: 350,
                marginX: 1,
                marginY: 0.5,
                display: "flex",
                alignItems: "center",
                borderRadius: 8,
                paddingX: 1,
                cursor: "pointer",
                overflow: "hidden",
              }}
            >
              <Typography level="body-lg" fontWeight="bold">
                Events
              </Typography>
            </Box>
          )}
          <TabLink
            value="profile"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid"
            to={
              competitionUuid
                ? "/gym/$venueUuid/events/$competitionUuid/profile"
                : "/gym/$venueUuid/events/create"
            }
            params={(prev) => ({ ...prev, competitionUuid })}
            replace
          >
            <ListItemDecorator>
              <Article />
            </ListItemDecorator>
            Profile
          </TabLink>
          <TabLink
            value="routes"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid/events/$competitionUuid"
            to="./routes"
            replace
            disabled={!competitionUuid}
          >
            <ListItemDecorator>
              <Map />
            </ListItemDecorator>
            Routes
          </TabLink>
          <TabLink
            value="competitors"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid/events/$competitionUuid"
            to="./competitors"
            replace
            disabled={!competitionUuid}
          >
            <ListItemDecorator>
              <People />
            </ListItemDecorator>
            Competitors
          </TabLink>
          <TabLink
            value="tools"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid/events/$competitionUuid"
            to="./tools"
            replace
            disabled={!competitionUuid}
          >
            <ListItemDecorator>
              <Construction />
            </ListItemDecorator>
            Tools
          </TabLink>
        </TabList>
      </Tabs>
      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        {competitionSelectorOpen && (
          <CompetitionsList
            activeCompetitionUuid={competitionUuid}
            activeLeagueUuid={competition?.league?.uuid || league?.uuid}
            isCreating={isCreate}
            creatingEventType={isCreate ? "competition" : undefined}
          />
        )}
        <Box sx={{ flex: 1, display: "flex", minWidth: 0 }}>{children || <Outlet />}</Box>
      </Stack>
    </Box>
  );
};

type ListItem =
  | {
      type: "competition";
      competition: ApiCompetition;
    }
  | {
      type: "league";
      league: ApiLeagueSummary | ApiLeague;
      competitions: ApiCompetition[];
    };

const CompetitionListItem = ({
  item,
  isAddingRound,
}: {
  item: ListItem;
  isAddingRound?: boolean;
}) => {
  if (item.type === "competition") {
    return <CompetitionCard competition={item.competition} />;
  } else {
    const hasEmptyCategories = "categories" in item.league && item.league.categories.length === 0;
    const addRoundButton = (
      <ButtonLink
        to="/gym/$venueUuid/events/create"
        from="/gym/$venueUuid"
        search={{ series: item.league.uuid }}
        startDecorator={<Add />}
        color="neutral"
        variant="soft"
        disabled={hasEmptyCategories}
      >
        Add round
      </ButtonLink>
    );
    return (
      <Stack gap={1}>
        <LeagueCard league={item.league} />
        <Stack gap={1} marginLeft={3.5}>
          {item.competitions.map((comp) => (
            <CompetitionCard key={comp.uuid} competition={comp} />
          ))}
          {isAddingRound ? <CompetitionCard creatingEventType="round" /> : null}
          {!isAddingRound &&
          (item.league.endTime === null || new Date(item.league.endTime).getTime() > Date.now()) ? (
            <Box
              sx={{
                marginBottom: 1,
                display: "flex",
                justifyContent: "center",
                marginRight: 3.5,
              }}
            >
              {hasEmptyCategories ? (
                <Tooltip title="Add categories to the series first">
                  <Box>{addRoundButton}</Box>
                </Tooltip>
              ) : (
                addRoundButton
              )}
            </Box>
          ) : null}
        </Stack>
      </Stack>
    );
  }
};

const CompetitionsList = ({
  activeCompetitionUuid,
  activeLeagueUuid,
  isCreating,
  creatingEventType,
}: {
  activeCompetitionUuid?: string;
  activeLeagueUuid?: string;
  isCreating?: boolean;
  creatingEventType?: "competition" | "league";
}) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: competition } = useCompetitionQuery(activeCompetitionUuid, currentVenueUuid);
  const { data: competitions, isPending: competitionsIsPending } = useVenueCompetitionsQuery(
    currentVenueUuid,
    undefined,
    { allPages: true },
  );

  const { data: league } = useLeagueQuery(activeLeagueUuid, currentVenueUuid);

  const { data: leagues, isPending: leaguesIsPending } = useVenueLeaguesQuery(currentVenueUuid);

  const stackRef = useRef<HTMLDivElement>(null);

  const mergedLeaguesByUuid = useMemo(() => {
    if (!competitions) {
      return undefined;
    }

    const result: Record<
      string,
      { league?: ApiLeagueSummary | ApiLeague; comps: ApiCompetition[] }
    > = {};

    competitions.forEach((comp) => {
      if (!comp.league) {
        result[""] = result[""] || { comps: [] };
        result[""].comps.push(comp);
      } else {
        result[comp.league.uuid] = result[comp.league.uuid] || {
          league: comp.league,
          comps: [],
        };
        result[comp.league.uuid].comps.push(comp);
      }
    });

    leagues?.forEach((league) => {
      if (!result[league.uuid]) {
        result[league.uuid] = {
          comps: [],
          league: {} as ApiLeagueSummary,
        };
      }
      result[league.uuid].league = league;
      league.competitions.forEach((comp) => {
        if (result[league.uuid].comps.some((c) => c.uuid === comp.uuid)) {
          return;
        }
        result[league.uuid].comps.push({
          ...comp,
          league,
        });
      });
    });

    return result;
  }, [competitions, leagues]);

  const itemsByStatus = useMemo(() => {
    const result: Record<string, ListItem[]> = {};
    if (!mergedLeaguesByUuid) {
      return result;
    }

    for (const { league, comps } of Object.values(mergedLeaguesByUuid)) {
      if (!league) {
        comps.forEach((comp) => {
          result[comp.status] = result[comp.status] || [];
          result[comp.status].push({
            type: "competition",
            competition: comp,
          });
        });
      } else {
        const status = league.status;
        result[status] = result[status] || [];
        result[status].push({
          type: "league",
          league,
          competitions: orderBy(comps, "startTime"),
        });
      }
    }
    Object.keys(result).forEach((status) => {
      result[status] = orderBy(
        result[status],
        (item) =>
          item.type === "competition" ? item.competition.startTime : item.league.startTime,
        ["desc"],
      );
    });
    return result;
  }, [mergedLeaguesByUuid]);

  const [selectedStatus, setSelectedStatus] = useState<ApiCompetition["status"] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!selectedStatus) {
      if (competition) {
        if (competition.league?.status) {
          setSelectedStatus(competition.league.status);
        } else {
          setSelectedStatus(competition.status);
        }
      } else if (league) {
        setSelectedStatus(league.status);
      } else if (isCreating) {
        setSelectedStatus("UPCOMING");
      } else if (itemsByStatus) {
        setSelectedStatus(Object.keys(itemsByStatus)[0] as ApiCompetition["status"]);
      }
    }
  }, [selectedStatus, itemsByStatus, competition, league, isCreating]);

  useEffect(() => {
    if (!activeCompetitionUuid && !activeLeagueUuid) {
      if (isCreating) {
        // Scroll to the bottom
        stackRef.current?.scrollTo({
          top: stackRef.current.scrollHeight,
        });
      }
      return;
    }
    if (competitionsIsPending || leaguesIsPending) {
      return;
    }
    const el: HTMLElement | undefined | null = stackRef.current?.querySelector(
      `[data-uuid="${activeCompetitionUuid || activeLeagueUuid}"]`,
    );
    if (!el) {
      // Wait for render
      setTimeout(() => {
        const el: HTMLElement | undefined | null = stackRef.current?.querySelector(
          `[data-uuid="${activeCompetitionUuid || activeLeagueUuid}"]`,
        );
        if (!el) {
          return;
        }
        stackRef.current?.scrollTo({
          top: el.offsetTop - stackRef.current.offsetTop - 20,
        });
      }, 0);
      return;
    }
    stackRef.current?.scrollTo({
      top: el.offsetTop - stackRef.current.offsetTop - 20,
    });
  }, [
    activeCompetitionUuid,
    activeLeagueUuid,
    isCreating,
    competitionsIsPending,
    leaguesIsPending,
  ]);

  const navigate = useNavigate();
  useDisplayGenericModal();

  const selectPillsOptions = itemsByStatus
    ? Object.keys(itemsByStatus).map((status) => ({
        value: status,
        name: getCompetitionStatusLabel(status as ApiCompetition["status"]),
      }))
    : [];

  if (isCreating && !activeLeagueUuid && !itemsByStatus["UPCOMING"]) {
    selectPillsOptions.push({
      value: "UPCOMING",
      name: "Upcoming",
    });
  }

  return (
    <Stack id={EVENTS_LIST_ID} sx={{ width: 360, minWidth: 360 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          padding: 1,
          zIndex: 10,
          backgroundColor: "white",
        }}
      >
        <PillsSelect
          options={selectPillsOptions}
          value={selectedStatus ?? null}
          onChange={(value) => value && setSelectedStatus(value as ApiCompetition["status"])}
        />
      </Box>
      <Stack
        gap={1}
        sx={{ paddingX: 1, paddingBottom: 1, flex: 1, overflowY: "auto" }}
        ref={stackRef}
      >
        {itemsByStatus?.[selectedStatus || ""]?.map((item) => (
          <CompetitionListItem
            key={item.type === "competition" ? item.competition.uuid : item.league.uuid}
            isAddingRound={
              item.type === "league" && item.league.uuid === activeLeagueUuid && isCreating
            }
            item={item}
          />
        ))}
        {selectedStatus === "UPCOMING" && isCreating && !activeLeagueUuid ? (
          <CompetitionCard
            creatingEventType={
              creatingEventType === "competition" && activeLeagueUuid ? "round" : creatingEventType
            }
          />
        ) : null}
      </Stack>
      <Box sx={{ margin: 1, display: "flex" }}>
        <Dropdown>
          <MenuButton variant="solid" color="primary" startDecorator={<Add />} sx={{ flex: 1 }}>
            Create Event
          </MenuButton>
          <Menu>
            <MenuItem
              onClick={() => {
                navigate({
                  to: "/gym/$venueUuid/events/create",
                  from: "/gym/$venueUuid",
                });
              }}
            >
              New competition
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate({
                  to: "/gym/$venueUuid/events/series/create",
                  from: "/gym/$venueUuid",
                });
              }}
            >
              New series
            </MenuItem>
          </Menu>
        </Dropdown>
      </Box>
    </Stack>
  );
};

// Export CompetitionsList component so it can be used in LeagueOverview
export { CompetitionsList };

export default CompetitionOverview;
